import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

console.log('🔧 API Configuration - Backend URL:', BACKEND_URL);
console.log('🔧 Full API Base URL:', `${BACKEND_URL}/api`);

export const apiClient = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 10000,
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      window.location.href = '/auth';
    }
    return Promise.reject(error);
  }
);

// API methods
export const api = {
  // Stats
  getStats: () => apiClient.get('/stats'),
  
  // Auth
  requestAuthLink: (identifier) => apiClient.post('/auth/request', { identifier }),
  verifyAuthToken: (token) => apiClient.post(`/auth/verify/${token}`),
  loginWithPassword: (identifier, password) => apiClient.post('/auth/login', { identifier, password }),
  register: (userData) => apiClient.post('/auth/register', userData),
  verifyEmail: (token) => apiClient.post('/auth/verify-email', { token }),
  verifyPhone: (token) => apiClient.post('/auth/verify-phone', { token }),
  resendVerification: (emailOrPhone) => apiClient.post('/auth/resend-verification', { email_or_phone: emailOrPhone }),
  
  // User
  getUserProfile: () => apiClient.get('/user/profile'),
  getUserQRCodes: () => apiClient.get('/user/qr-codes'),
  getUserMessages: (qrCodeId) => apiClient.get('/user/messages', { params: { qr_code_id: qrCodeId } }),
  replyToMessage: (messageId, content) => apiClient.post(`/user/messages/${messageId}/reply`, { content }),
  
  // QR Codes
  getQRCodeInfo: (uid) => apiClient.get(`/qr/${uid}`),
  claimQRCode: (uid, data) => apiClient.post(`/qr/${uid}/claim`, data),
  claimQRCodePublic: (uid, claimData) => apiClient.post(`/qr/${uid}/claim-public`, claimData),
  generateQRCode: (data) => apiClient.post('/qr/generate', data),
  updateQRCode: (uid, qrData) => apiClient.put(`/user/qr-codes/${uid}`, qrData),
  deleteQRCode: (uid) => apiClient.delete(`/user/qr-codes/${uid}`),
  sendMessageToOwner: (uid, messageData) => apiClient.post(`/qr/${uid}/message`, messageData),
  
  // Notification Contacts
  getNotificationContacts: (uid) => apiClient.get(`/qr/${uid}/notification-contacts`),
  updateNotificationContacts: (uid, contactsData) => apiClient.put(`/qr/${uid}/notification-contacts`, contactsData),
  
  // Subscription
  upgradeSubscription: (plan) => apiClient.post('/subscription/upgrade', { plan }),
  cancelSubscription: () => apiClient.post('/subscription/cancel'),
  getSubscriptionPlans: () => apiClient.get('/subscription/plans'),
  
  // Admin
  getAdminStats: () => apiClient.get('/admin/stats'),
  getAdminUsers: (page = 1, limit = 50, search = '') => apiClient.get('/admin/users', { params: { page, limit, search } }),
  updateUserSubscription: (userId, subscriptionData) => apiClient.put(`/admin/users/${userId}`, subscriptionData),
  getAdminMessages: (page = 1, limit = 50, search = '') => apiClient.get('/admin/messages', { params: { page, limit, search } }),
  getSystemSettings: () => apiClient.get('/admin/system-settings'),
  updateSystemSettings: (settings) => apiClient.put('/admin/system-settings', settings),
  testSMS: (phoneNumber) => apiClient.post('/admin/test-sms', { phone_number: phoneNumber }),
  toggleUserStatus: (userId) => apiClient.post(`/admin/users/${userId}/toggle-status`),
  createAdminUser: (adminData) => apiClient.post('/admin/create-admin', adminData),
  bulkGenerateQR: (data) => apiClient.post('/admin/bulk-generate-qr', data),
  getQRBatches: (page = 1, limit = 20) => apiClient.get('/admin/qr-batches', { params: { page, limit } }),
  getBatchQRCodes: (batchId) => apiClient.get(`/admin/qr-batch/${batchId}/codes`),
  markBatchPrinted: (batchId) => apiClient.post(`/admin/qr-batch/${batchId}/mark-printed`),
  downloadBatchCSV: (batchId) => apiClient.get(`/admin/qr-batch/${batchId}/download-csv`, { responseType: 'blob' }),
  
  // Store
  getQRPacks: () => apiClient.get('/store/qr-packs'),
  purchaseQRPack: (packData) => apiClient.post('/store/purchase-qr-pack', packData),
  completePayment: (orderId, paymentData) => apiClient.post(`/store/complete-payment/${orderId}`, paymentData),
  downloadQRPack: (orderId) => apiClient.get(`/store/download-qr-pack/${orderId}`),
  getUserPurchases: () => apiClient.get('/user/purchases'),
  
  // Physical Products
  getPhysicalProducts: () => apiClient.get('/store/products'),
  createProduct: (productData) => apiClient.post('/admin/products', productData),
  getAdminProducts: () => apiClient.get('/admin/products'),
  updateProduct: (productId, productData) => apiClient.put(`/admin/products/${productId}`, productData),
  deleteProduct: (productId) => apiClient.delete(`/admin/products/${productId}`),
  
  // QR Generation for Store
  generateQRPoster: () => apiClient.post('/qr/generate-poster', {}, { responseType: 'blob' }),
  generatePublicCustomerServicePoster: (businessName, customText) => 
    apiClient.post('/public/generate-customer-service-poster', { 
      business_name: businessName, 
      custom_text: customText 
    }),
  generatePublicCustomerServicePosterWord: (businessName, customText) => 
    apiClient.post('/public/generate-customer-service-poster-word', { 
      business_name: businessName, 
      custom_text: customText 
    }),
  generateSingleQR: () => apiClient.get('/qr/generate-single', { responseType: 'blob' }),
  
  // Database Backup & Restore
  downloadDatabaseBackup: () => apiClient.get('/admin/backup/download', { responseType: 'blob' }),
  restoreDatabaseBackup: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/admin/backup/restore', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getBackupInfo: () => apiClient.get('/admin/backup/info'),
  
  // Order Management
  getOrders: () => apiClient.get('/admin/orders'),
  updateOrderStatus: (orderId, statusData) => apiClient.put(`/admin/orders/${orderId}/status`, statusData),
  getShippingLabel: (orderId) => apiClient.get(`/admin/orders/${orderId}/shipping-label`),
  
  // Message Reply
  getMessage: (messageId) => apiClient.get(`/messages/${messageId}`),
  sendReplyToMessage: (qrCodeId, messageId, replyData) => apiClient.post(`/qr/${qrCodeId}/messages/${messageId}/reply`, replyData),
  
  // Contact Form
  submitContactForm: (contactData) => apiClient.post('/contact', contactData),
};